import React from 'react';
import { LuArrowRight, LuLockOpen } from 'react-icons/lu';
import { COPY } from '../../contracts/copyMap';
import { CONVERSION_EVENTS, useTrackViewOnce } from '../../analytics/conversion';
import useStartTrial from '../../hooks/useStartTrial';
import AurumButton from '../oracle/AurumButton';

// The standing offer card. One presentation for every full-size paywall in the
// product — the routine sneak peek, the Glowup peer-feedback gate, and the
// membership page.
//
//   ╭──────────────────────╮
//   │        ( 🔓 )        │  open padlock — the work is DONE and waiting,
//   │      MOGGED PRO      │  the door is what opens
//   │   <the one sentence> │
//   │     $4.99 / MONTH    │  optional
//   │ [ BEGIN FREE TRIAL → ]│
//   │   trial terms        │
//   │   merchant-of-record │  ← see below, this one is not optional
//   ╰──────────────────────╯
//
// ⚠ NO CENSOR BAR HERE, and no "ACCESS RESTRICTED". The paywall used to open
// with a black LABEL bar reading ACCESS RESTRICTED, which framed a finished
// product as a locked door and led with the denial. The gold eyebrow says who
// is selling; the headline says what is waiting. Do not put the bar back.
//
// ⚠ THE MOR NOTE IS A COMPLIANCE CONTROL, NOT FOOTER TEXT. Under Stripe
// Managed Payments the charge reads LINK.COM on the customer's statement and
// receipts arrive from Link, not MOGGED. An unrecognised statement line is the
// largest driver of avoidable chargebacks, and chargebacks threaten Managed
// Payments eligibility itself. It must render BEFORE checkout starts, which
// means on every surface whose button reaches createCheckoutSession. Pass
// morNote={false} only where another copy of the disclosure is already on
// screen.
export default function ProOfferPanel({
  headline,
  body,
  price,
  eyebrow = COPY.MEMBERSHIP_EYEBROW_PRO,
  ctaLabel = COPY.MEMBERSHIP_CTA,
  terms = COPY.MEMBERSHIP_TRIAL_NOTE,
  morNote = true,
  label = 'ProOfferPanel',
  // Each surface keeps its own CTA test id. The id names the surface a test is
  // driving, not the component that happens to render it — so extracting this
  // card did not invalidate the suites that already covered those paywalls.
  ctaTestId = 'pro-offer-cta',
}) {
  const { startTrial, submitting, error } = useStartTrial(label);

  // Conversion funnel: every full-size paywall renders through this card, so
  // one view event here covers the routine sneak peek, the Glowup gates and
  // the membership page. `label` already names the surface for useStartTrial's
  // console.error — the same string keys paywall_viewed to trial_cta_clicked.
  useTrackViewOnce(CONVERSION_EVENTS.PAYWALL_VIEWED, { surface: label, gate: 'pro_offer' });

  return (
    // `w-full max-w-[560px]`, NOT `w-[560px] max-w-full`. Every caller centres
    // this inside a flex container, and a flex item's default `min-width: auto`
    // refuses to shrink below min-content — so the fixed-width version blew
    // past 375px and clipped the headline off both edges of the phone.
    //
    // Gutters step down below `sm` too: at that width the wider padding
    // squeezed the CTA enough to wrap its label and strand the trailing arrow.
    <div
      className="w-full min-w-0 max-w-[560px] rounded-modal border border-aurum/25 bg-obsidian-raised px-5 py-10 text-center shadow-[0_50px_120px_-24px_rgba(0,0,0,0.95),0_0_60px_rgba(201,164,92,0.10)] sm:px-14 sm:py-11"
      data-testid="pro-offer-panel"
    >
      <div className="mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full border border-aurum/70 bg-obsidian/60 shadow-[0_0_30px_rgba(201,164,92,0.35)]">
        <LuLockOpen className="text-aurum" size={29} strokeWidth={1.6} aria-hidden="true" />
      </div>

      <div className="mt-7 font-censor text-[13px] font-semibold uppercase tracking-[0.3em] text-aurum">
        {eyebrow}
      </div>

      <h2 className="m-0 mt-[14px] font-display text-[30px] font-light leading-[1.12] tracking-[0.01em] text-bone sm:text-[36px]">
        {headline}
      </h2>

      {body && (
        <p className="mx-auto m-0 mt-[18px] max-w-[400px] font-display text-[17px] leading-[1.5] text-bone-dim">
          {body}
        </p>
      )}

      {price && (
        <p className="m-0 mt-[18px] font-display text-[19px] tracking-[0.14em] text-bone-dim">
          {price}
        </p>
      )}

      <div className="mt-9">
        <AurumButton
          size="lg"
          fullWidth
          onClick={startTrial}
          loading={submitting}
          trailingIcon={<LuArrowRight size={17} />}
          className="whitespace-nowrap"
          data-testid={ctaTestId}
        >
          {ctaLabel}
        </AurumButton>
      </div>

      {terms && (
        <p className="mx-auto m-0 mt-[15px] max-w-[380px] font-display text-[13.5px] leading-relaxed text-bone-faint">
          {terms}
        </p>
      )}

      {morNote && (
        <p className="mx-auto m-0 mt-[14px] max-w-[380px] font-display text-[11.5px] leading-relaxed text-bone-dim">
          {COPY.MEMBERSHIP_MOR_NOTE}
        </p>
      )}

      {/* Bone, never red — DESIGN.md forbids red even for errors. */}
      {error && (
        <p className="m-0 mt-4 font-display text-sm text-bone" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
